// FILE: src/store/Slices/GroupSlice.jsx (FULLY UPDATED)
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosServices from "../../services/axios";
import { setCalendarFilters } from "./CalendarFilterSlice";
import { fetchInstagramProfile, fetchfacebookProfile } from "../../services/group";
import { fetchAssets } from "./librarySlice";
import { getProfileAvatarUrl, withResolvedProfileAvatar } from "../../utils/profileAvatar";

const getStoredProfileDoc = (allProfiles, platform, profileId) => {
  return (allProfiles || []).find((profile) => {
    if (profile.platform !== platform) return false;
    if (String(profile.user_id || "") === String(profileId)) return true;
    if (String(profile.platform_id || "") === String(profileId)) return true;

    const pageDetails = Array.isArray(profile.page_details) ? profile.page_details : [];
    return pageDetails.some(
      (detail) =>
        String(detail?.accountId || "") === String(profileId) ||
        String(detail?.pageId || "") === String(profileId)
    );
  }) || null;
};

const getStoredPageDetail = (profileDoc, profileId = null) => {
  const pageDetails = Array.isArray(profileDoc?.page_details) ? profileDoc.page_details : [];
  if (!profileId) return pageDetails[0] || {};

  return (
    pageDetails.find(
      (detail) =>
        String(detail?.accountId || "") === String(profileId) ||
        String(detail?.pageId || "") === String(profileId)
    ) ||
    pageDetails[0] ||
    {}
  );
};

const getFacebookStoredPageDetail = (facebookMasterProfiles, userId, pageId) => {
  const masterProfile = (facebookMasterProfiles || []).find(
    (profile) =>
      profile.platform === "FACEBOOK" &&
      (
        String(profile.user_id || "") === String(userId) ||
        String(profile.platform_id || "") === String(userId)
      )
  );

  const pageDetails = Array.isArray(masterProfile?.page_details) ? masterProfile.page_details : [];
  return (
    pageDetails.find(
      (detail) =>
        String(detail?.pageId || "") === String(pageId) ||
        String(detail?.accountId || "") === String(pageId)
    ) || {}
  );
};

const getYoutubeStoredPageDetail = (masterYouTubeProfile, channelId) => {
  const pageDetails = Array.isArray(masterYouTubeProfile?.page_details)
    ? masterYouTubeProfile.page_details
    : [];

  return (
    pageDetails.find(
      (detail) =>
        String(detail?.pageId || "") === String(channelId) ||
        String(detail?.accountId || "") === String(channelId)
    ) || {}
  );
};

const normalizeProfileDetails = (details = {}) => {
  const merged = {
    ...details,
    name:
      details.name ||
      details.profile ||
      details.business_name ||
      details.username ||
      null,
    username:
      details.username ||
      details.business_name ||
      details.profile ||
      details.name ||
      null,
  };

  const resolved = withResolvedProfileAvatar(merged);
  const resolvedAvatarUrl = getProfileAvatarUrl(resolved);

  return {
    ...resolved,
    profile_picture_url:
      resolvedAvatarUrl ||
      resolved.profile_picture_url ||
      resolved.profile_picture_source_url ||
      resolved.profile_image ||
      null,
    profile_image:
      resolvedAvatarUrl ||
      resolved.profile_image ||
      resolved.profile_picture_url ||
      null,
  };
};

export const handleAuthSuccess = createAsyncThunk(
  "groups/handleAuthSuccess",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(fetchGroups()).unwrap();
      await dispatch(fetchAllProfileDetails()).unwrap();
      sessionStorage.removeItem("temp_selected_group_for_auth");
      return { success: true };
    } catch (error) {
      console.error("Error during post-auth data refresh:", error);
      sessionStorage.removeItem("temp_selected_group_for_auth");
      return rejectWithValue(
        error.message || "Failed to refresh data after connection."
      );
    }
  }
);

export const fetchAllProfileDetails = createAsyncThunk(
  "groups/fetchAllProfileDetails",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { groups } = getState().groups;
      if (!groups || groups.length === 0) return {};

      const [allProfilesResponse, facebookProfilesResponse] = await Promise.all([
        axiosServices.get("/profiles/get-profile"),
        axiosServices
          .post("/profiles/get-platform-profile", { platform: "FACEBOOK" })
          .catch(() => ({ data: { profiles: [] } })),
      ]);

      const allProfiles = allProfilesResponse?.data?.profiles || [];
      const facebookMasterProfiles = facebookProfilesResponse?.data?.profiles || [];
      const masterYouTubeProfile = allProfiles.find(
        (profile) => profile.platform === "YOUTUBE_MASTER"
      );
      const youtubeRefreshToken = masterYouTubeProfile?.refresh_token;

      const allAccounts = groups.flatMap((group) => group.connected_accounts || []);
      const uniqueProfileIds = new Map();

      allAccounts.forEach((account) => {
        const key = String(
account.platform === "FACEBOOK"
? account.platform_pageId
: account.platform_id
);

        if (key && !uniqueProfileIds.has(key)) {
          uniqueProfileIds.set(key, account);
        }
      });

      const detailPromises = Array.from(uniqueProfileIds.values()).map(
        async (account) => {
          const key = String(
account.platform === "FACEBOOK"
? account.platform_pageId
: account.platform_id
);

          try {
            let details;

            if (account.platform === "INSTAGRAM") {
              const data = await fetchInstagramProfile(account.platform_id);
              const storedProfile = getStoredProfileDoc(
                allProfiles,
                "INSTAGRAM",
                account.platform_id
              );
              const storedPage = getStoredPageDetail(storedProfile, account.platform_id);

              details = normalizeProfileDetails({
                ...data,
                name: data?.name || data?.username || storedPage?.profile || account.profile,
                username: data?.username || data?.name || storedPage?.profile || account.profile,
                profile_picture_url:
                  data?.profile_picture_url ||
                  storedPage?.profile_picture_source_url ||
                  storedPage?.profile_picture_url ||
                  null,
                profile_picture_source_url:
                  data?.profile_picture_source_url ||
                  storedPage?.profile_picture_source_url ||
                  storedPage?.profile_picture_url ||
                  null,
                profile_picture_blob_url:
                  data?.profile_picture_blob_url ||
                  storedPage?.profile_picture_blob_url ||
                  null,
                profile_picture_blob_name:
                  data?.profile_picture_blob_name ||
                  storedPage?.profile_picture_blob_name ||
                  null,
                profile_picture_synced_at:
                  data?.profile_picture_synced_at ||
                  storedPage?.profile_picture_synced_at ||
                  null,
                profile_picture_version:
                  data?.profile_picture_version ||
                  storedPage?.profile_picture_version ||
                  null,
              });
            } else if (account.platform === "PINTEREST") {
              const response = await axiosServices.post("/pinterest/get-profile", {
                user_id: account.platform_id,
              });

              const apiRoot = response?.data || {};

              if (!apiRoot?.success || !apiRoot?.userAccount) {
                throw new Error("Invalid Pinterest profile response shape");
              }

              const userAccount = apiRoot.userAccount;

              const storedProfile = getStoredProfileDoc(
                allProfiles,
                "PINTEREST",
                account.platform_id
              );
              const storedPage = getStoredPageDetail(storedProfile, account.platform_id);

              const resolvedName =
                userAccount?.name ||
                userAccount?.business_name ||
                userAccount?.username ||
                storedPage?.profile ||
                account.profile ||
                "Pinterest";

              details = normalizeProfileDetails({
                ...storedPage,
                ...userAccount,
                id:
                  userAccount?.id ||
                  storedPage?.accountId ||
                  storedPage?.pageId ||
                  account.platform_id,
                name: resolvedName,
                username:
                  userAccount?.username ||
                  userAccount?.business_name ||
                  userAccount?.name ||
                  storedPage?.profile ||
                  account.profile ||
                  "Pinterest",
                business_name:
                  userAccount?.business_name ||
                  storedPage?.profile ||
                  account.profile ||
                  null,
                profile:
                  storedPage?.profile ||
                  userAccount?.business_name ||
                  userAccount?.username ||
                  account.profile ||
                  null,
                profile_image:
                  userAccount?.profile_picture_blob_url ||
                  userAccount?.profile_image ||
                  storedPage?.profile_picture_blob_url ||
                  storedPage?.profile_picture_source_url ||
                  storedPage?.profile_picture_url ||
                  null,
                profile_picture_url:
                  userAccount?.profile_picture_url ||
                  userAccount?.profile_image ||
                  storedPage?.profile_picture_source_url ||
                  storedPage?.profile_picture_url ||
                  null,
                profile_picture_source_url:
                  userAccount?.profile_picture_source_url ||
                  userAccount?.profile_image ||
                  storedPage?.profile_picture_source_url ||
                  storedPage?.profile_picture_url ||
                  null,
                profile_picture_blob_url:
                  userAccount?.profile_picture_blob_url ||
                  storedPage?.profile_picture_blob_url ||
                  null,
                profile_picture_blob_name:
                  userAccount?.profile_picture_blob_name ||
                  storedPage?.profile_picture_blob_name ||
                  null,
                profile_picture_synced_at:
                  userAccount?.profile_picture_synced_at ||
                  storedPage?.profile_picture_synced_at ||
                  null,
                profile_picture_version:
                  userAccount?.profile_picture_version ||
                  storedPage?.profile_picture_version ||
                  null,
              });
            } else if (account.platform === "LINKEDIN") {
  const storedProfile = getStoredProfileDoc(
    allProfiles,
    "LINKEDIN",
    account.platform_id
  );
  const storedPage = getStoredPageDetail(storedProfile, account.platform_id);
  if (storedPage && Object.keys(storedPage).length > 0) {
    details = normalizeProfileDetails({
      ...storedPage,
      name: storedPage.profile || account.profile,
      username: storedPage.profile || account.profile,
      profile_picture_url:
        storedPage.profile_picture_source_url ||
        storedPage.profile_picture_url ||
        null,
    });
  } else {
    details = normalizeProfileDetails({
      name: account.profile,
      username: account.profile,
      error: "Details not found",
    });
  }
} else if (account.platform === "LINKEDIN_COMPANY") {
  const storedProfile = getStoredProfileDoc(
    allProfiles,
    "LINKEDIN_COMPANY",
    account.platform_id
  );
  const storedPage = getStoredPageDetail(storedProfile, account.platform_id);

  if (storedPage && Object.keys(storedPage).length > 0) {
    details = normalizeProfileDetails({
      ...storedPage,
      id: account.platform_id,
      name: storedPage.profile || account.profile,
      username: storedPage.profile || account.profile,
      profile: storedPage.profile || account.profile,
      profile_picture_url:
        storedPage.profile_picture_blob_url ||
        storedPage.profile_picture_source_url ||
        storedPage.profile_picture_url ||
        null,
      profile_picture_source_url:
        storedPage.profile_picture_source_url ||
        storedPage.profile_picture_url ||
        null,
      profile_picture_blob_url:
        storedPage.profile_picture_blob_url || null,
      profile_picture_blob_name:
        storedPage.profile_picture_blob_name || null,
      profile_picture_synced_at:
        storedPage.profile_picture_synced_at || null,
      profile_picture_version:
        storedPage.profile_picture_version || null,
    });
  } else {
    details = normalizeProfileDetails({
      id: account.platform_id,
      name: account.profile,
      username: account.profile,
      profile: account.profile,
      error: "Details not found",
    });
  }
} else if (account.platform === "GMB") {
              const storedProfile = getStoredProfileDoc(
                allProfiles,
                "GMB",
                account.platform_id
              );
              const storedPage = getStoredPageDetail(storedProfile, account.platform_id);

              details = normalizeProfileDetails({
                ...storedPage,
                name: storedPage.profile || account.profile,
                username: storedPage.profile || account.profile,
                profile_picture_url:
                  storedPage.profile_picture_source_url ||
                  storedPage.profile_picture_url ||
                  null,
              });
            } else if (account.platform === "YOUTUBE") {
              const storedProfile = getStoredProfileDoc(
                allProfiles,
                "YOUTUBE",
                account.platform_id
              );
              const storedPage =
                getStoredPageDetail(storedProfile, account.platform_id) ||
                getYoutubeStoredPageDetail(masterYouTubeProfile, account.platform_id);

              if (!youtubeRefreshToken) {
                details = normalizeProfileDetails({
                  name: storedPage?.profile || account.profile,
                  username: storedPage?.profile || account.profile,
                  profile_picture_url:
                    storedPage?.profile_picture_source_url ||
                    storedPage?.profile_picture_url ||
                    null,
                  profile_picture_blob_url:
                    storedPage?.profile_picture_blob_url || null,
                });
              } else {
                const { data } = await axiosServices.post(
                  "/youtube/channel-details",
                  {
                    channelId: account.platform_id,
                    refreshToken: youtubeRefreshToken,
                  }
                );

                details = normalizeProfileDetails({
                  ...data,
                  name: data?.title || storedPage?.profile || account.profile,
                  username: data?.title || storedPage?.profile || account.profile,
                  profile_picture_url:
                    data?.thumbnails?.high?.url ||
                    data?.thumbnails?.medium?.url ||
                    data?.thumbnails?.default?.url ||
                    storedPage?.profile_picture_source_url ||
                    storedPage?.profile_picture_url ||
                    null,
                  profile_picture_source_url:
                    storedPage?.profile_picture_source_url ||
                    storedPage?.profile_picture_url ||
                    data?.thumbnails?.high?.url ||
                    data?.thumbnails?.medium?.url ||
                    data?.thumbnails?.default?.url ||
                    null,
                  profile_picture_blob_url:
                    storedPage?.profile_picture_blob_url || null,
                  profile_picture_blob_name:
                    storedPage?.profile_picture_blob_name || null,
                  profile_picture_synced_at:
                    storedPage?.profile_picture_synced_at || null,
                  profile_picture_version:
                    storedPage?.profile_picture_version || null,
                });
              }
            } else if (account.platform === "TIKTOK") {
              const storedProfile = getStoredProfileDoc(
                allProfiles,
                "TIKTOK",
                account.platform_id
              );
              const storedPage = getStoredPageDetail(storedProfile, account.platform_id);

              if (storedPage && Object.keys(storedPage).length > 0) {
                details = normalizeProfileDetails({
                  ...storedPage,
                  name: storedPage.profile || account.profile,
                  username: storedPage.profile || account.profile,
                  profile_picture_url:
                    storedPage.profile_picture_source_url ||
                    storedPage.profile_picture_url ||
                    null,
                });
              } else {
                details = normalizeProfileDetails({
                  name: account.profile,
                  username: account.profile,
                  error: "Details not found",
                });
              }
            }

            return { key, details };
          } catch (error) {
            console.warn(
              `Could not fetch details for profile ${key}:`,
              error.message
            );
            const errorType = error.response?.data?.errorType || null;

            return {
              key,
              details: normalizeProfileDetails({
                name: account.profile || "Profile Error",
                username: account.profile || "Profile Error",
                error: errorType,
              }),
            };
          }
        }
      );

      const results = await Promise.all(detailPromises);

      const profileDetailsMap = results.reduce((acc, { key, details }) => {
        if (key && details) {
          acc[String(key)] = details;
        }
        return acc;
      }, {});

      return profileDetailsMap;
    } catch (error) {
      console.error("[GroupSlice] fetchAllProfileDetails failed:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch profile details."
      );
    }
  }
);

export const fetchGroups = createAsyncThunk(
  "groups/fetchGroups",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosServices.get("/group/get");
      const fetchedGroups = response.data.groups || [];
      return fetchedGroups;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const createGroup = createAsyncThunk(
  "groups/createGroup",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosServices.post("/group/create", payload);
      if (response.data.success) {
        return response.data;
      }
      return rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const deleteGroupDetails = createAsyncThunk(
  "groups/deleteGroupDetails",
  async (platform_id, { rejectWithValue }) => {
    try {
      const response = await axiosServices.post("/group/delete-profile", {
        platform_id,
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting group details:", error);
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

export const fetchGroupDetails = createAsyncThunk(
  "groups/fetchGroupDetails",
  async (groupName, { dispatch, rejectWithValue }) => {
    if (!groupName) {
      return rejectWithValue("Group name is required to fetch details.");
    }

    try {
      const response = await axiosServices.post("/group/get-particular", {
        groupName,
      });

      const accounts = response.data.group.connected_accounts || [];
      const uniqueProfiles = accounts;

      const allProfileIds = uniqueProfiles
        .map((profile) =>
          profile.platform === "FACEBOOK"
            ? profile.platform_pageId
            : profile.platform_id
        )
        .filter(Boolean);

      dispatch(setCalendarFilters({ profiles: allProfileIds }));
      return uniqueProfiles;
    } catch (error) {
      return rejectWithValue(error.response?.data || "An error occurred");
    }
  }
);

const groupSlice = createSlice({
  name: "groups",
  initialState: {
    groups: [],
    loading: false,
    selectedGroup: localStorage.getItem("selected_group") || null,
    profile: [],
    profileDetails: {},
    error: null,
  },
  reducers: {
    _setSelectedGroup: (state, action) => {
      const groupName = action.payload;
      state.selectedGroup = groupName;

      if (groupName) {
        localStorage.setItem("selected_group", groupName);
      } else {
        localStorage.removeItem("selected_group");
      }
    },
    clearGroups: (state) => {
      state.groups = [];
      state.error = null;
    },
    setProfiles: (state, action) => {
      state.profile = action.payload;
    },
    updateGroupAndProfiles: (state, action) => {
      const updatedGroup = action.payload;
      if (!updatedGroup || !updatedGroup._id) return;

      const groupIndex = state.groups.findIndex(
        (group) => group._id === updatedGroup._id
      );

      if (groupIndex !== -1) {
        state.groups[groupIndex] = updatedGroup;
      }

      if (state.selectedGroup === updatedGroup.name) {
        state.profile = updatedGroup.connected_accounts;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch groups";
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        if (action.payload.success) {
          const updatedGroup = action.payload.group;
          const groupIndex = state.groups.findIndex(
            (group) => group._id === updatedGroup._id
          );

          if (groupIndex !== -1) {
            state.groups[groupIndex] = updatedGroup;
          } else {
            state.groups.push(updatedGroup);
          }

          if (state.selectedGroup === updatedGroup.name) {
            state.profile = updatedGroup.connected_accounts;
          }
        }
      })
      .addCase(fetchGroupDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGroupDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(fetchGroupDetails.rejected, (state, action) => {
        state.loading = false;
        state.profile = [];
        state.error = action.payload;
      })
      .addCase(deleteGroupDetails.fulfilled, () => {})
      .addCase(fetchAllProfileDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllProfileDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.profileDetails = { ...state.profileDetails, ...action.payload };
      })
      .addCase(fetchAllProfileDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch profile details";
      });
  },
});

export const {
  clearGroups,
  _setSelectedGroup,
  setProfiles,
  updateGroupAndProfiles,
} = groupSlice.actions;

export const setSelectedGroup = (groupName) => (dispatch, getState) => {
  dispatch(_setSelectedGroup(groupName));
  const { groups } = getState().groups;

  if (groupName) {
    const selectedGroupData = groups.find((group) => group.name === groupName);
    if (selectedGroupData?._id) {
      dispatch(fetchAssets(selectedGroupData._id));
    } else {
      console.warn(
        `[GroupSlice] Could not find group ID for "${groupName}" to fetch assets.`
      );
    }
  }
};

export default groupSlice.reducer;